/**
 * Общие конфигурации для ECharts графиков
 * Используется во всех отчётах: registration, order, payment, ad, webinar
 */

import { formatCellData } from '../../../../../helpers/utils/reports';
import {
    getAxisGroupForMetric,
    getAxisGroupById,
    AXIS_GROUP_COLORS,
    type AxisGroup,
} from './MetricAxisGroups';
import { TrendType } from '../../../../../app/providers/redux/slices/reports/chart/types';
import { isCompareMetricKey, toBaseMetricKey } from '../../../constants/compare';

// Цвета для графиков
export const CHART_COLORS = [
    '#339dff', '#00b379', '#ffc233', '#d52f40', '#5d3faa',
    '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7',
    '#2E93fA', '#35be6a', '#546E7A', '#E91E63', '#FF9800'
];

// ============================================
// Линейная регрессия для расчёта тренда
// ============================================

interface TrendResult {
    trendData: Array<[string | number, number]>;
    slope: number;
    intercept: number;
    percentChange: number; // Процент изменения от начала к концу
}

/**
 * Расчёт линейной регрессии методом наименьших квадратов
 * @param data - массив точек [x, y] где x - индекс, y - значение
 * @returns объект с данными тренда и коэффициентами
 */
const calculateLinearRegression = (
    data: Array<{ x: string | number; y: number }>
): TrendResult | null => {
    if (!data || data.length < 2) return null;

    const n = data.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;

    // Используем индексы как X для расчёта (0, 1, 2, ...)
    data.forEach((point, index) => {
        const x = index;
        const y = Number(point.y) || 0;
        sumX += x;
        sumY += y;
        sumXY += x * y;
        sumX2 += x * x;
    });

    const denominator = n * sumX2 - sumX * sumX;
    if (denominator === 0) return null;

    const slope = (n * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / n;

    // Генерируем точки линии тренда
    const trendData: Array<[string | number, number]> = data.map((point, index) => {
        const trendY = slope * index + intercept;
        return [point.x, trendY];
    });

    // Вычисляем процент изменения
    const startValue = intercept;
    const endValue = slope * (n - 1) + intercept;
    const percentChange = startValue !== 0 
        ? ((endValue - startValue) / Math.abs(startValue)) * 100 
        : 0;

    return {
        trendData,
        slope,
        intercept,
        percentChange,
    };
};

/**
 * Расчёт скользящего среднего (Moving Average)
 * Для каждой точки считает среднее за последние `window` значений
 */
const calculateMovingAverage = (
    data: Array<{ x: string | number; y: number }>,
    window: number
): TrendResult | null => {
    if (!data || data.length < window || window < 2) return null;

    const trendData: Array<[string | number, number]> = [];

    for (let i = window - 1; i < data.length; i++) {
        let sum = 0;
        for (let j = i - window + 1; j <= i; j++) {
            sum += Number(data[j].y) || 0;
        }
        trendData.push([data[i].x, sum / window]);
    }

    if (trendData.length < 2) return null;

    const startValue = trendData[0][1];
    const endValue = trendData[trendData.length - 1][1];
    const percentChange = startValue !== 0
        ? ((endValue - startValue) / Math.abs(startValue)) * 100
        : 0;

    return {
        trendData,
        slope: 0,
        intercept: 0,
        percentChange,
    };
};

/**
 * Автоматический подбор окна MA (~20% от длины данных, min 3, max 30)
 */
const getAutoMAWindow = (dataLength: number): number => {
    const window = Math.round(dataLength * 0.2);
    return Math.max(3, Math.min(window, 30));
};

/**
 * Универсальная функция расчёта тренда — направляет к нужной математике по типу
 */
const calculateTrend = (
    data: Array<{ x: string | number; y: number }>,
    trendType: TrendType
): TrendResult | null => {
    switch (trendType) {
        case 'linear':
            return calculateLinearRegression(data);
        case 'ma':
            return calculateMovingAverage(data, getAutoMAWindow(data.length));
        case 'ma7':
            return calculateMovingAverage(data, 7);
        case 'ma14':
            return calculateMovingAverage(data, 14);
        case 'ma30':
            return calculateMovingAverage(data, 30);
        case 'average':
            // Средняя линия реализуется через markLine, не через отдельную серию
            return null;
        default:
            return calculateLinearRegression(data);
    }
};

/**
 * Форматирование процента изменения для отображения в легенде
 */
const formatTrendPercent = (percent: number): string => {
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(1)}%`;
};

/** Названия типов трендов для легенды */
const TREND_TYPE_LABELS: Record<TrendType, string> = {
    linear: 'тренд',
    ma: 'MA',
    ma7: 'MA(7)',
    ma14: 'MA(14)',
    ma30: 'MA(30)',
    average: 'среднее',
};

/**
 * Создание серии тренда для ECharts
 * @param seriesIndex - индекс серии для смещения метки (чтобы метки не перекрывались)
 * @param trendType - тип тренда (влияет на стиль линии)
 */
const createTrendSeries = (
    originalSeries: any,
    trendResult: TrendResult,
    color: string,
    yAxisIndex = 0,
    seriesIndex = 0,
    trendType: TrendType = 'linear'
): any => {
    const typeLabel = TREND_TYPE_LABELS[trendType] || 'тренд';
    const trendName = `${originalSeries.name || 'Данные'} (${typeLabel} ${formatTrendPercent(trendResult.percentChange)})`;
    const percentLabel = formatTrendPercent(trendResult.percentChange);
    const isPositive = trendResult.percentChange >= 0;
    const labelColor = isPositive ? '#00b379' : '#d52f40';

    // MA рисуется сглаженной кривой, линейный — прямой
    const isMA = trendType === 'ma' || trendType === 'ma7' || trendType === 'ma14' || trendType === 'ma30';

    // Смещаем позицию метки для каждой серии, чтобы они не перекрывались
    const positionOffsets = [0.5, 0.4, 0.6, 0.35, 0.65];
    const positionRatio = positionOffsets[seriesIndex % positionOffsets.length];
    const pointIndex = Math.floor((trendResult.trendData.length - 1) * positionRatio);
    const labelPoint = trendResult.trendData[pointIndex];

    return {
        name: trendName,
        type: 'line',
        smooth: isMA, // MA — сглаженная кривая, линейный — прямая
        symbol: 'none',
        yAxisIndex,
        lineStyle: {
            width: 2,
            type: 'dashed',
            opacity: isMA ? 0.8 : 0.7,
        },
        itemStyle: {
            color,
        },
        markPoint: {
            silent: true,
            symbol: 'rect',
            symbolSize: [50, 20],
            itemStyle: {
                color: 'rgba(255, 255, 255, 0.9)',
                borderColor: labelColor,
                borderWidth: 1,
            },
            label: {
                show: true,
                formatter: percentLabel,
                fontSize: 11,
                fontWeight: 'bold',
                color: labelColor,
            },
            data: [
                {
                    coord: labelPoint,
                },
            ],
        },
        data: trendResult.trendData,
        _isTrend: true,
        _trendType: trendType,
        _percentChange: trendResult.percentChange,
        _originalSeriesName: originalSeries.name,
        _forSeriesIndex: seriesIndex,
    };
};

/**
 * Создание markLine для средней линии (type: 'average')
 * Добавляется к основной серии, а не как отдельная серия
 */
const createAverageMarkLine = (color: string) => ({
    silent: true,
    symbol: 'none',
    data: [{ type: 'average', name: 'Среднее' }],
    lineStyle: {
        type: 'dashed',
        color,
        opacity: 0.6,
        width: 2,
    },
    label: {
        show: true,
        position: 'insideEndTop',
        formatter: (params: any) => {
            const val = params.value;
            return typeof val === 'number' ? val.toFixed(1) : val;
        },
        fontSize: 20,
        fontWeight: 'bold',
        color,
    },
});

// Базовые настройки tooltip для ограничения внутри контейнера
const BASE_TOOLTIP_STYLE = 'max-width:280px;max-height:260px;overflow:auto;white-space:normal;word-break:break-word;';

// В сравнении подсказка — таблица «метрика × период», ей нужно больше места
const COMPARE_TOOLTIP_STYLE = 'max-width:460px;max-height:320px;overflow:auto;white-space:normal;word-break:break-word;';

/**
 * Осветление hex-цвета подмешиванием белого.
 * Нужно для сравнения: линии обоих периодов сплошные, второй период — светлее
 */
export const lightenColor = (hex: string, ratio = 0.45): string => {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex).trim());
    if (!match) return hex;

    const channels = [ match[1], match[2], match[3] ].map((part) => {
        const value = parseInt(part, 16);
        const lightened = Math.round(value + (255 - value) * ratio);
        return lightened.toString(16).padStart(2, '0');
    });

    return `#${channels.join('')}`;
};

// Базовые настройки для всех графиков
export const getBaseChartOption = () => ({
    animation: false,
    progressive: 500,
    progressiveThreshold: 1000,
});

// Интерфейс для серии с информацией о метрике
interface SeriesWithMetric {
    name: string;
    dataKey?: string;
    label?: string; // Название метрики для легенды
    data?: Array<{ x: string | number; y: number }>;
}

/**
 * Настройки отображения для метрики на графике
 */
export interface MetricDisplaySettings {
    dataKey: string;
    showTrend?: boolean;  // Показывать линию тренда (по умолчанию true)
    hideLine?: boolean;   // Скрыть основной график (по умолчанию false)
    trendType?: TrendType; // Переопределение типа тренда для конкретной метрики (если не задан — используется defaultTrendType)
}

// Функция для извлечения названия метрики из полного названия серии
const extractMetricName = (series: SeriesWithMetric): string => {
    // Если есть label, используем его
    if (series.label) {
        // label может быть React элементом, извлекаем текст
        if (typeof series.label === 'object' && series.label !== null) {
            const labelObj = series.label as { props?: { children?: string[] } };
            return labelObj.props?.children?.[0] || series.name;
        }
        return String(series.label);
    }
    
    // Иначе пытаемся извлечь название метрики из name (формат: "Метрика (группа)")
    const match = series.name?.match(/^(.+?)\s*\(/);
    if (match) {
        return match[1].trim();
    }
    
    return series.name || '';
};

// Конфигурация для линейного графика с поддержкой множественных осей Y
export const getLineChartOption = (
    series: any[],
    colors: string[] = CHART_COLORS,
    isCompare = false,
    useMultipleAxes = true,
    showLegend = true,
    xAxisName = '', // Название оси X (например "Дата регистрации")
    showAxes = false, // Показывать ли оси Y (по умолчанию скрыты)
    showTrend: boolean | MetricDisplaySettings[] = true, // Показывать линию тренда: boolean для глобального или массив настроек для каждой метрики
    chartHeight = 450, // Высота графика для адаптации названий осей
    defaultTrendType: TrendType = 'linear', // Тип тренда по умолчанию для всех метрик
    alignByIndex = false // Режим сравнения: выровнять серии по номеру точки, а не по дате
) => {
    // Преобразуем showTrend в функцию для проверки настроек метрики
    const getMetricSettings = (dataKey: string): { showTrend: boolean; hideLine: boolean; trendType: TrendType } => {
        if (typeof showTrend === 'boolean') {
            return { showTrend, hideLine: false, trendType: defaultTrendType };
        }
        // Ищем настройки для конкретной метрики
        const settings = showTrend.find(s => s.dataKey === dataKey);
        return {
            showTrend: settings?.showTrend !== false, // По умолчанию true
            hideLine: settings?.hideLine === true,   // По умолчанию false
            trendType: settings?.trendType || defaultTrendType, // Индивидуальный или глобальный
        };
    };
    if (!series.length || !series[0]?.data) {
        return null;
    }

    // Серия второго периода сравнения: метрика получает служебный суффикс в dataKey
    // и "(2)" в подписи — "Регистрации (2) (Итого)" (см. ReportChart)
    const SECOND_PERIOD_IN_NAME = /\s*\(2\)\s*(?=\([^()]*\)\s*$)/;
    const isSecondPeriodSeries = (s: any): boolean =>
        isCompareMetricKey(String(s?.dataKey || '')) || SECOND_PERIOD_IN_NAME.test(String(s?.name || ''));

    // Ключ пары «метрика × группировка»: обе серии одной метрики за разные периоды
    // должны получить один цвет, поэтому из имени убираем пометку периода
    const getPairKey = (s: any): string => {
        const name = String(s?.name || '');
        if (name) return name.replace(SECOND_PERIOD_IN_NAME, ' ');

        return toBaseMetricKey(String(s?.dataKey || ''));
    };

    // В режиме сравнения периоды бывают разной длины, поэтому каждый растягивается
    // на всю ширину: ось X — доля прохождения периода в процентах (0…100).
    // Так начало сопоставляется с началом, конец — с концом, а длина периодов не мешает.
    // Реальные даты сохраняем в _xLabels и показываем только в подсказке.
    const chartSeries = alignByIndex
        ? series.map((s: any) => {
            const points = s.data ?? [];
            const isSecondPeriod = isSecondPeriodSeries(s);

            // Период из одной точки (например, «эта неделя» = один день) линией не рисуется —
            // растягиваем единственное значение на всю ширину, чтобы уровень было видно
            if (points.length === 1) {
                return {
                    ...s,
                    _xLabels: [ points[0].x, points[0].x ],
                    _isSecondPeriod: isSecondPeriod,
                    _isSinglePoint: true,
                    data: [ { ...points[0], x: 0 }, { ...points[0], x: 100 } ],
                };
            }

            const lastIndex = Math.max(points.length - 1, 1);
            return {
                ...s,
                _xLabels: points.map((d: any) => d.x),
                _isSecondPeriod: isSecondPeriod,
                data: points.map((d: any, i: number) => ({ ...d, x: (i / lastIndex) * 100 })),
            };
        })
        : series;

    // Подпись точки в подсказке: в обычном режиме — категория оси, в сравнении — своя дата серии
    const getPointLabel = (seriesInfo: any, dataIndex: number): string => {
        const label = seriesInfo?._xLabels?.[dataIndex];
        return label != null ? String(label) : '';
    };

    // Ближайшая к позиции курсора точка серии. В сравнении шаг у периодов разный
    // (14 дней против 7), поэтому точки не совпадают по оси и ECharts отдаёт в подсказку
    // только одну серию — ближайшие точки остальных ищем сами
    const findNearestPointIndex = (data: any[], axisValue: number): number => {
        let bestIndex = -1;
        let bestDistance = Infinity;

        (data || []).forEach((point: any, index: number) => {
            const x = Number(Array.isArray(point) ? point[0] : point?.x);
            if (!isFinite(x)) return;

            const distance = Math.abs(x - axisValue);
            if (distance < bestDistance) {
                bestDistance = distance;
                bestIndex = index;
            }
        });

        return bestIndex;
    };

    // Имя серии приходит как "Метрика (группировка)", у второго периода — "Метрика (2) (группировка)".
    // Для подсказки метрику и группировку показываем отдельно
    const splitSeriesName = (name: string): { metric: string; group: string } => {
        const raw = String(name || '');
        const match = raw.match(/^(.*)\s*\(([^()]*)\)\s*$/);

        if (!match) return { metric: raw.replace(SECOND_PERIOD_IN_NAME, ' ').trim(), group: '' };

        return {
            metric: match[1].replace(/\s*\(2\)\s*$/, '').trim(),
            group: match[2].trim(),
        };
    };

    interface CompareTooltipCell {
        value: number;
        dateLabel: string;
        series: any;
    }

    interface CompareTooltipRow {
        metric: string;
        group: string;
        color: string;
        first?: CompareTooltipCell;
        second?: CompareTooltipCell;
    }

    // Подсказка в режиме сравнения: таблица «метрика × период».
    // Периоды стоят в соседних колонках, даты вынесены в шапку (в одной вертикали
    // графика дата у всех метрик периода одна), справа — отклонение второго от первого
    const buildCompareTooltip = (
        allSeries: any[],
        axisValue: number,
        formatValue: (value: number, seriesInfo: any) => string
    ): string => {
        const rows = new Map<string, CompareTooltipRow>();

        allSeries.forEach((s: any) => {
            if (s._isTrend) return;

            const index = findNearestPointIndex(s.data, axisValue);
            if (index < 0) return;

            const point = s.data[index];
            const identity = { name: s._originalName, dataKey: s._dataKey };
            const pairKey = getPairKey(identity);
            const { metric, group } = splitSeriesName(s._originalName);
            const cell: CompareTooltipCell = {
                value: Number(Array.isArray(point) ? point[1] : point?.y),
                dateLabel: getPointLabel(s, index),
                series: s,
            };

            const row: CompareTooltipRow = rows.get(pairKey) ?? { metric, group, color: s?.itemStyle?.color };
            if (isSecondPeriodSeries(identity)) {
                row.second = cell;
            } else {
                row.first = cell;
                // Насыщенный цвет — у линии первого периода, его и берём для маркера
                row.color = s?.itemStyle?.color;
            }
            rows.set(pairKey, row);
        });

        const rowList = Array.from(rows.values());
        if (rowList.length === 0) return '';

        // Даты периодов одинаковы для всех метрик в этой точке — показываем их один раз в шапке
        const firstDate = rowList.map((row) => row.first?.dateLabel).find(Boolean) || '';
        const secondDate = rowList.map((row) => row.second?.dateLabel).find(Boolean) || '';

        // Общая для всех строк группировка выносится подзаголовком, чтобы не повторяться
        const groups = [ ...new Set(rowList.map((row) => row.group).filter(Boolean)) ];
        const commonGroup = groups.length === 1 ? groups[0] : '';

        const headerCell = 'text-align: right; padding: 0 0 6px 12px; font-weight: normal; color: #8c8c8c; white-space: nowrap;';
        const valueCell = 'text-align: right; padding: 3px 0 3px 12px; white-space: nowrap;';

        let result = `<div style="font-weight: bold;">${Math.round(axisValue)}% периода</div>`;
        if (commonGroup) {
            result += `<div style="color: #8c8c8c; margin-top: 2px;">${commonGroup}</div>`;
        }

        result += `<table style="border-collapse: collapse; font-size: 12px; margin-top: 8px;">
            <tr>
                <th style="${headerCell} text-align: left; padding-left: 0;"></th>
                <th style="${headerCell}">${firstDate}</th>
                <th style="${headerCell}">${secondDate}</th>
                <th style="${headerCell}"></th>
            </tr>`;

        rowList.forEach((row) => {
            const name = commonGroup || !row.group ? row.metric : `${row.metric} (${row.group})`;
            const firstValue = row.first ? formatValue(row.first.value, row.first.series) : '—';
            const secondValue = row.second ? formatValue(row.second.value, row.second.series) : '—';

            let delta = '';
            if (row.first && row.second && isFinite(row.first.value) && isFinite(row.second.value)) {
                const diff = row.second.value - row.first.value;
                const color = diff >= 0 ? '#00b379' : '#d52f40';
                const percent = row.first.value !== 0
                    ? ` (${formatTrendPercent((diff / Math.abs(row.first.value)) * 100)})`
                    : '';
                delta = `<span style="color: ${color};">${diff >= 0 ? '▲ +' : '▼ '}${String(formatCellData(diff))}${percent}</span>`;
            }

            result += `<tr>
                <td style="${valueCell} text-align: left; padding-left: 0;">
                    <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${row.color}; margin-right: 6px;"></span>${name}
                </td>
                <td style="${valueCell}">${firstValue}</td>
                <td style="${valueCell}">${secondValue}</td>
                <td style="${valueCell}">${delta}</td>
            </tr>`;
        });

        return `${result}</table>`;
    };

    // Цвет линии: в сравнении пара «период 1 / период 2» одной метрики красится одинаково,
    // в обычном режиме — как раньше, по порядковому индексу серии
    const pairColorMap = new Map<string, string>();
    let pairColorIndex = 0;
    const getSeriesColor = (s: any, index: number): string => {
        if (!alignByIndex) return colors[index % colors.length];

        const pairKey = getPairKey(s);
        if (!pairColorMap.has(pairKey)) {
            pairColorMap.set(pairKey, colors[pairColorIndex % colors.length]);
            pairColorIndex++;
        }

        // Обе линии пары сплошные, поэтому второй период отличаем светлым оттенком
        if (isSecondPeriodSeries(s)) {
            return lightenColor(pairColorMap.get(pairKey) as string);
        }
        return pairColorMap.get(pairKey) as string;
    };

    // Собираем информацию о группах осей для каждой серии
    // Используем axisGroup из метрики, если он доступен, иначе определяем по dataKey/name
    const seriesAxisInfo = chartSeries.map((s: SeriesWithMetric & { axisGroup?: number }) => {
        const groupId = s.axisGroup ?? getAxisGroupForMetric(s.dataKey || s.name || '');
        const group = getAxisGroupById(groupId);
        return { series: s, groupId, group };
    });

    // Получаем уникальные группы осей
    const uniqueGroups = [...new Set(seriesAxisInfo.map(info => info.groupId))];
    const groupToAxisIndex = new Map<number, number>();
    uniqueGroups.forEach((groupId, index) => {
        groupToAxisIndex.set(groupId, index);
    });

    // Собираем информацию о метриках для каждой группы осей (с цветами)
    const groupMetricInfo = new Map<number, Array<{ name: string; color: string; seriesIndex: number }>>();
    seriesAxisInfo.forEach((info, seriesIndex) => {
        const metricName = extractMetricName(info.series);
        if (!groupMetricInfo.has(info.groupId)) {
            groupMetricInfo.set(info.groupId, []);
        }
        const metrics = groupMetricInfo.get(info.groupId)!;
        // Проверяем, что метрика ещё не добавлена
        if (!metrics.find(m => m.name === metricName)) {
            metrics.push({
                name: metricName,
                color: getSeriesColor(info.series, seriesIndex),
                seriesIndex,
            });
        }
    });

    // Функция для форматирования названия оси с rich text (цветные названия метрик)
    const CHAR_WIDTH_PX = 6.5;
    const CHART_PADDING_PX = 80;
    
    const formatAxisNameRich = (metrics: Array<{ name: string; color: string }>, chartHeight = 400): { 
        name: string; 
        lineCount: number;
        rich: Record<string, { color: string; fontSize: number; fontWeight: string }>;
    } => {
        const availableHeight = chartHeight - CHART_PADDING_PX;
        const maxCharsPerLine = Math.floor(availableHeight / CHAR_WIDTH_PX);
        
        // Создаём rich стили для каждой метрики
        const rich: Record<string, { color: string; fontSize: number; fontWeight: string }> = {};
        metrics.forEach((metric, idx) => {
            rich[`m${idx}`] = {
                color: metric.color,
                fontSize: 10,
                fontWeight: 'bold',
            };
        });
        
        // Собираем строки с rich text разметкой
        const lines: string[] = [];
        let currentLine = '';
        let currentLineLength = 0;
        
        metrics.forEach((metric, idx) => {
            const separator = currentLine ? ', ' : '';
            const testLength = currentLineLength + separator.length + metric.name.length;
            
            if (testLength <= maxCharsPerLine) {
                currentLine += separator + `{m${idx}|${metric.name}}`;
                currentLineLength = testLength;
            } else {
                if (currentLine) {
                    lines.push(currentLine);
                }
                currentLine = `{m${idx}|${metric.name}}`;
                currentLineLength = metric.name.length;
            }
        });
        
        if (currentLine) {
            lines.push(currentLine);
        }
        
        return { name: lines.join('\n'), lineCount: lines.length, rich };
    };

    // Создаём конфигурации осей Y
    // Рассчитываем offset для правых осей так, чтобы они были у края
    const rightAxesCount = Math.max(0, uniqueGroups.length - 1);
    const AXIS_SPACING = 70; // Расстояние между осями (увеличено для предотвращения наложения названий)
    
    // Вычисляем min/max для каждой группы осей, чтобы выровнять нулевые линии
    const groupMinMax = new Map<number, { min: number; max: number }>();
    seriesAxisInfo.forEach(info => {
        const values = info.series.data?.map((d: any) => Number(d.y)).filter((v: number) => !isNaN(v)) || [];
        if (values.length === 0) return;
        
        const currentMinMax = groupMinMax.get(info.groupId) || { min: Infinity, max: -Infinity };
        const seriesMin = Math.min(...values);
        const seriesMax = Math.max(...values);
        
        groupMinMax.set(info.groupId, {
            min: Math.min(currentMinMax.min, seriesMin),
            max: Math.max(currentMinMax.max, seriesMax),
        });
    });
    
    // Выравнивание нулевых линий для всех осей
    // Находим позицию нуля в процентах от диапазона для каждой оси
    const hasNegativeValues = Array.from(groupMinMax.values()).some(({ min }) => min < 0);
    const hasPositiveValues = Array.from(groupMinMax.values()).some(({ max }) => max > 0);
    
    // Вычисляем выровненные min/max для каждой оси
    const alignedMinMax = new Map<number, { min: number | undefined; max: number | undefined }>();
    
    if (hasNegativeValues && hasPositiveValues && uniqueGroups.length > 1) {
        // Находим максимальную долю отрицательной и положительной части
        let maxNegativePart = 0; // Максимальная доля отрицательной части (|min| / (|min| + max))
        let maxPositivePart = 0; // Максимальная доля положительной части (max / (|min| + max))
        
        groupMinMax.forEach(({ min, max }) => {
            const absMin = Math.abs(Math.min(0, min));
            const absMax = Math.max(0, max);
            const total = absMin + absMax;
            if (total > 0) {
                maxNegativePart = Math.max(maxNegativePart, absMin / total);
                maxPositivePart = Math.max(maxPositivePart, absMax / total);
            }
        });
        
        // Функция для округления до целого числа (в большую по модулю сторону)
        const roundAwayFromZero = (value: number): number => {
            return value >= 0 
                ? Math.ceil(value) 
                : Math.floor(value);
        };
        
        // Теперь для каждой оси вычисляем новые min/max так, чтобы ноль был на одной позиции
        groupMinMax.forEach(({ min, max }, groupId) => {
            const absMin = Math.abs(Math.min(0, min));
            const absMax = Math.max(0, max);
            
            // Новый диапазон должен соответствовать пропорции maxNegativePart : maxPositivePart
            // При этом сохраняем реальные данные внутри диапазона
            let newMin: number | undefined;
            let newMax: number | undefined;
            
            if (maxNegativePart > 0 && maxPositivePart > 0) {
                // Вычисляем новые границы на основе общей пропорции
                // Если ось не имеет отрицательных значений, расширяем вниз
                // Если ось не имеет достаточно положительных значений, расширяем вверх
                const targetRatio = maxNegativePart / maxPositivePart;
                
                if (absMax > 0) {
                    // Есть положительные значения — вычисляем min на основе max
                    const requiredNegative = absMax * targetRatio;
                    if (requiredNegative > absMin) {
                        // Округляем до 1 знака после запятой (в большую по модулю сторону)
                        newMin = roundAwayFromZero(-requiredNegative);
                    }
                }
                
                if (absMin > 0) {
                    // Есть отрицательные значения — вычисляем max на основе min
                    const requiredPositive = absMin / targetRatio;
                    if (requiredPositive > absMax) {
                        // Округляем до 1 знака после запятой (в большую сторону)
                        newMax = roundAwayFromZero(requiredPositive);
                    }
                }
            }
            
            alignedMinMax.set(groupId, { min: newMin, max: newMax });
        });
    }
    
    const yAxisConfigs = uniqueGroups.map((groupId, index) => {
        const isRight = index > 0; // Первая ось слева, остальные справа
        const metrics = groupMetricInfo.get(groupId) || [];
        const { name: axisName, lineCount, rich } = formatAxisNameRich(metrics, chartHeight);
        
        // Для правых осей: последняя ось у края (offset=0), остальные смещаются влево
        const rightAxisIndex = index - 1; // 0, 1, 2... для правых осей
        const rightOffset = isRight ? (rightAxesCount - 1 - rightAxisIndex) * AXIS_SPACING : 0;
        
        // Получаем выровненные min/max для оси
        const aligned = alignedMinMax.get(groupId);
        
        return {
            type: 'value',
            name: showAxes ? axisName : '', // Показываем название только если оси видны
            nameLocation: 'middle', // Название по центру оси
            nameRotate: isRight ? -90 : 90, // Вертикальное расположение названия
            nameGap: showAxes ? 40 + (lineCount - 1) * 5 : 0, // Отступ от оси до названия (увеличен для предотвращения наложения)
            nameTextStyle: {
                fontSize: 10,
                fontWeight: 'bold',
                lineHeight: 14,
                rich, // Rich text стили для цветных названий метрик
            },
            position: isRight ? 'right' : 'left',
            offset: showAxes ? rightOffset : 0,
            // Выравнивание нулевых линий: устанавливаем min/max только если нужно расширить диапазон
            min: aligned?.min,
            max: aligned?.max,
            axisLine: {
                show: showAxes,
                lineStyle: {
                    color: '#666',
                },
            },
            axisLabel: {
                show: showAxes,
                fontSize: 10,
                formatter: (value: number) => {
                    // Для процентов добавляем %
                    if (groupId === 6 || groupId === 10 || groupId === 11) {
                        return `${formatCellData(value)}%`;
                    }
                    // Для сумм форматируем в тысячи
                    if (groupId === 4 && value >= 1000) {
                        return `${(value / 1000).toFixed(0)}K`;
                    }
                    return formatCellData(value);
                },
                color: '#666',
            },
            splitLine: {
                show: index === 0, // Линии сетки только для первой оси
                lineStyle: {
                    color: '#eee',
                },
            },
        };
    });

    // Если только одна группа или отключены множественные оси, используем конфигурацию с одной осью
    if (!useMultipleAxes || uniqueGroups.length <= 1) {
        const singleGroupId = uniqueGroups[0] || 1;
        
        // Собираем информацию о метриках для оси с цветами
        const metrics = groupMetricInfo.get(singleGroupId) || [];
        const { name: axisName, lineCount, rich } = formatAxisNameRich(metrics, chartHeight);
        
        const simpleSeriesData: any[] = [];

        chartSeries.forEach((s: any, index: number) => {
            const legendName = extractMetricName(s);
            const seriesColor = getSeriesColor(s, index); // Цвет линии (в сравнении — общий для пары периодов)
            const isPlan = s.name?.startsWith('План');
            const isSecondPeriod = Boolean(s._isSecondPeriod);
            
            // Получаем настройки отображения для этой метрики
            const metricSettings = getMetricSettings(s.dataKey || s.name || '');
            
            // Основная серия данных (скрываем если hideLine = true)
            if (!metricSettings.hideLine) {
                simpleSeriesData.push({
                    name: legendName,
                    type: 'line',
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 6,
                    lineStyle: {
                        width: isSecondPeriod ? 2.5 : 2,
                        // Обе линии сравнения сплошные: второй период отличается светлым оттенком
                        type: (isPlan && !isSecondPeriod) ? 'dashed' : 'solid',
                    },
                    itemStyle: {
                        color: seriesColor,
                    },
                    data: s.data?.map((d: any) => [d.x, Number(d.y)]) || [],
                    _originalName: s.name,
                    _seriesIndex: index, // Индекс для связи с трендом
                    _dataKey: s.dataKey, // Сохраняем dataKey для идентификации
                    _xLabels: s._xLabels, // Реальные даты точек (режим сравнения)
                });
            }
            
            // Добавляем линию тренда (только для не-плановых серий, не накопительных и если включён тренд для этой метрики)
            const isCumulative = s.dataKey?.includes('_cumulative') || s.name?.includes('_cumulative');
            if (metricSettings.showTrend && !isPlan && !isCumulative && !s._isSinglePoint && s.data?.length >= 2) {
                const effectiveTrendType = metricSettings.trendType;

                if (effectiveTrendType === 'average') {
                    // Средняя линия — добавляем markLine к последней добавленной серии
                    const lastSeries = simpleSeriesData[simpleSeriesData.length - 1];
                    if (lastSeries) {
                        lastSeries.markLine = createAverageMarkLine(seriesColor);
                    }
                } else {
                    const trendResult = calculateTrend(s.data, effectiveTrendType);
                    if (trendResult) {
                        simpleSeriesData.push(createTrendSeries(
                            { name: legendName },
                            trendResult,
                            seriesColor,
                            0,
                            index,
                            effectiveTrendType
                        ));
                    }
                }
            }
        });

        const allDates = alignByIndex
            ? []
            : [...new Set(chartSeries.flatMap((s: any) => s.data?.map((d: any) => d.x) || []))];

        return {
            ...getBaseChartOption(),
            tooltip: {
                trigger: 'axis',
                confine: true,
                extraCssText: alignByIndex ? COMPARE_TOOLTIP_STYLE : BASE_TOOLTIP_STYLE,
                // enterable всегда: при множестве меток на подсказку можно навести мышь
                // и прокрутить скролл (иначе tooltip дёргается и скролл не поймать)
                enterable: true,
                axisPointer: {
                    type: 'cross',
                },
                formatter: (params: any) => {
                    if (!params || !params.length) return '';

                    if (alignByIndex) {
                        return buildCompareTooltip(
                            simpleSeriesData,
                            Number(params[0].axisValue ?? params[0].value?.[0]),
                            (value) => {
                                const formatted = String(formatCellData(value));
                                return (singleGroupId === 6 || singleGroupId === 10 || singleGroupId === 11)
                                    ? `${formatted}%`
                                    : formatted;
                            }
                        );
                    }

                    let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`;
                    params.forEach((param: any) => {
                        const seriesInfo = simpleSeriesData[param.seriesIndex];
                        // Пропускаем линии тренда в tooltip (они информативны только визуально)
                        if (seriesInfo?._isTrend) return;

                        let value = formatCellData(param.value[1]);
                        if (singleGroupId === 6 || singleGroupId === 10 || singleGroupId === 11) {
                            value += '%';
                        }
                        const displayName = seriesInfo?._originalName || param.seriesName;

                        // Ищем соответствующую серию тренда для отображения процента по индексу серии
                        const trendSeries = simpleSeriesData.find(
                            (s: any) => s._isTrend && s._forSeriesIndex === seriesInfo?._seriesIndex
                        );
                        const trendPercent = trendSeries?._percentChange;
                        const trendLabel = trendPercent !== undefined
                            ? ` <span style="color: ${trendPercent >= 0 ? '#00b379' : '#d52f40'};">(${formatTrendPercent(trendPercent)})</span>`
                            : '';

                        result += `<div style="display: flex; align-items: center; margin: 4px 0;">
                            <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${param.color}; margin-right: 8px;"></span>
                            <span>${displayName}: ${value}${trendLabel}</span>
                        </div>`;
                    });
                    return result;
                },
            },
        legend: {
            show: false, // Легенда отключена, используем названия осей
        },
        grid: {
            left: showAxes ? 55 : 15,
            right: 15,
            // Фиксированный отступ снизу: повёрнутые метки + название оси
            // В сравнении подписи не повёрнуты (проценты), поэтому отступ меньше
            bottom: alignByIndex ? 40 : 50,
            top: 15,
            containLabel: false,
        },
        xAxis: alignByIndex ? {
            // Сравнение: оба периода растянуты на всю ширину, шкала — % прохождения периода
            type: 'value',
            min: 0,
            max: 100,
            name: 'Прохождение периода — даты в подсказке',
            nameLocation: 'middle',
            nameGap: 26,
            nameTextStyle: {
                fontSize: 9,
                color: '#666',
            },
            axisLabel: {
                fontSize: 9,
                formatter: (value: number) => `${Math.round(value)}%`,
                color: '#666',
            },
            axisPointer: {
                label: {
                    formatter: (params: any) => `${Math.round(Number(params.value))}% периода`,
                },
            },
        } : {
            type: 'category',
            boundaryGap: false,
            data: allDates,
            name: xAxisName,
            nameLocation: 'middle',
            nameGap: 42,
            nameTextStyle: {
                fontSize: 9,
                color: '#666',
            },
            axisLabel: {
                rotate: 45,
                fontSize: 9,
                interval: 'auto',
            },
        },
        yAxis: {
            type: 'value',
            name: showAxes ? axisName : '',
            nameLocation: 'middle',
            nameRotate: 90,
            nameGap: showAxes ? 40 + (lineCount - 1) * 5 : 0,
            nameTextStyle: {
                fontSize: 10,
                fontWeight: 'bold',
                lineHeight: 14,
                rich,
            },
            axisLine: {
                show: showAxes,
                lineStyle: {
                    color: '#666',
                },
            },
            axisLabel: {
                show: showAxes,
                fontSize: 10,
                formatter: (value: number) => {
                    if (singleGroupId === 6 || singleGroupId === 10 || singleGroupId === 11) {
                        return `${formatCellData(value)}%`;
                    }
                    if (singleGroupId === 4 && value >= 1000) {
                        return `${(value / 1000).toFixed(0)}K`;
                    }
                    return formatCellData(value);
                },
                color: '#666',
            },
        },
            series: simpleSeriesData,
        };
    }

    // Создаём серии с привязкой к осям
    // Каждая линия получает свой уникальный цвет из палитры
    const seriesData: any[] = [];
    
    seriesAxisInfo.forEach((info, index) => {
        const axisIndex = groupToAxisIndex.get(info.groupId) || 0;
        const seriesColor = getSeriesColor(info.series, index); // Цвет линии (в сравнении — общий для пары периодов)
        const legendName = extractMetricName(info.series);
        const isPlan = info.series.name?.startsWith('План');
        const isSecondPeriod = Boolean((info.series as any)._isSecondPeriod);
        
        // Получаем настройки отображения для этой метрики
        const metricSettings = getMetricSettings(info.series.dataKey || info.series.name || '');
        
        // Основная серия данных (скрываем если hideLine = true)
        if (!metricSettings.hideLine) {
            seriesData.push({
                name: legendName, // Используем название метрики для легенды
                type: 'line',
                smooth: true,
                symbol: 'circle',
                symbolSize: 6,
                yAxisIndex: axisIndex,
                lineStyle: {
                    width: isSecondPeriod ? 2.5 : 2,
                    // Обе линии сравнения сплошные: второй период отличается светлым оттенком
                    type: (isPlan && !isSecondPeriod) ? 'dashed' : 'solid',
                },
                itemStyle: {
                    color: seriesColor, // Уникальный цвет для каждой линии
                },
                data: info.series.data?.map((d: any) => [d.x, Number(d.y)]) || [],
                // Сохраняем оригинальное имя для tooltip
                _originalName: info.series.name,
                _seriesIndex: index, // Индекс для связи с трендом
                _dataKey: info.series.dataKey, // Сохраняем dataKey для идентификации
                _xLabels: (info.series as any)._xLabels, // Реальные даты точек (режим сравнения)
            });
        }
        
        // Добавляем линию тренда (только для не-плановых серий, не накопительных и если включён тренд для этой метрики)
        const isCumulative = info.series.dataKey?.includes('_cumulative') || info.series.name?.includes('_cumulative');
        if (metricSettings.showTrend && !isPlan && !isCumulative && !(info.series as any)._isSinglePoint && info.series.data?.length >= 2) {
            const effectiveTrendType = metricSettings.trendType;

            if (effectiveTrendType === 'average') {
                // Средняя линия — добавляем markLine к последней добавленной серии
                const lastSeries = seriesData[seriesData.length - 1];
                if (lastSeries) {
                    lastSeries.markLine = createAverageMarkLine(seriesColor);
                }
            } else {
                const trendResult = calculateTrend(info.series.data, effectiveTrendType);
                if (trendResult) {
                    seriesData.push(createTrendSeries(
                        { name: legendName },
                        trendResult,
                        seriesColor,
                        axisIndex,
                        index,
                        effectiveTrendType
                    ));
                }
            }
        }
    });

    const allDates = alignByIndex
        ? []
        : [...new Set(chartSeries.flatMap((s: any) => s.data?.map((d: any) => d.x) || []))];

    return {
        ...getBaseChartOption(),
        tooltip: {
            trigger: 'axis',
            confine: true,
            extraCssText: alignByIndex ? COMPARE_TOOLTIP_STYLE : BASE_TOOLTIP_STYLE,
            // enterable всегда: при множестве меток на подсказку можно навести мышь
            // и прокрутить скролл (иначе tooltip дёргается и скролл не поймать)
            enterable: true,
            axisPointer: {
                type: 'cross',
            },
            formatter: (params: any) => {
                if (!params || !params.length) return '';

                if (alignByIndex) {
                    return buildCompareTooltip(
                        seriesData,
                        Number(params[0].axisValue ?? params[0].value?.[0]),
                        (value, seriesInfo) => {
                            const axisInfo = seriesAxisInfo.find(
                                (info) => info.series.name === seriesInfo?._originalName
                            );
                            const groupId = axisInfo?.groupId || 1;
                            const formatted = String(formatCellData(value));
                            return (groupId === 6 || groupId === 10 || groupId === 11)
                                ? `${formatted}%`
                                : formatted;
                        }
                    );
                }

                let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`;
                params.forEach((param: any) => {
                    const seriesInfo = seriesData[param.seriesIndex];
                    // Пропускаем линии тренда в tooltip (они информативны только визуально)
                    if (seriesInfo?._isTrend) return;
                    
                    // Находим соответствующую информацию об оси для форматирования
                    // Ищем в seriesAxisInfo по оригинальному имени серии
                    const axisInfo = seriesAxisInfo.find(info => 
                        extractMetricName(info.series) === param.seriesName ||
                        info.series.name === seriesInfo?._originalName
                    );
                    const groupId = axisInfo?.groupId || 1;
                    let value = formatCellData(param.value[1]);
                    
                    // Добавляем суффикс для процентов
                    if (groupId === 6 || groupId === 10 || groupId === 11) {
                        value += '%';
                    }
                    
                    // Используем оригинальное название серии (с группой) для tooltip
                    const displayName = seriesInfo?._originalName || param.seriesName;
                    
                    // Ищем соответствующую серию тренда для отображения процента по индексу серии
                    const trendSeries = seriesData.find(
                        (s: any) => s._isTrend && s._forSeriesIndex === seriesInfo?._seriesIndex
                    );
                    const trendPercent = trendSeries?._percentChange;
                    const trendLabel = trendPercent !== undefined
                        ? ` <span style="color: ${trendPercent >= 0 ? '#00b379' : '#d52f40'};">(${formatTrendPercent(trendPercent)})</span>`
                        : '';

                    result += `<div style="display: flex; align-items: center; margin: 4px 0;">
                        <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${param.color}; margin-right: 8px;"></span>
                        <span>${displayName}: ${value}${trendLabel}</span>
                    </div>`;
                });
                return result;
            },
        },
        legend: {
            show: false, // Легенда отключена, используем названия осей
        },
        grid: {
            left: showAxes ? 55 : 15,
            right: showAxes ? 55 + (rightAxesCount - 1) * AXIS_SPACING : 15,
            // Фиксированный отступ снизу: повёрнутые метки + название оси
            // В сравнении подписи не повёрнуты (проценты), поэтому отступ меньше
            bottom: alignByIndex ? 40 : 50,
            top: 15,
            containLabel: false,
        },
        xAxis: alignByIndex ? {
            // Сравнение: оба периода растянуты на всю ширину, шкала — % прохождения периода
            type: 'value',
            min: 0,
            max: 100,
            name: 'Прохождение периода — даты в подсказке',
            nameLocation: 'middle',
            nameGap: 26,
            nameTextStyle: {
                fontSize: 9,
                color: '#666',
            },
            axisLabel: {
                fontSize: 9,
                formatter: (value: number) => `${Math.round(value)}%`,
                color: '#666',
            },
            axisPointer: {
                label: {
                    formatter: (params: any) => `${Math.round(Number(params.value))}% периода`,
                },
            },
        } : {
            type: 'category',
            boundaryGap: false,
            data: allDates,
            name: xAxisName,
            nameLocation: 'middle',
            nameGap: 42,
            nameTextStyle: {
                fontSize: 9,
                color: '#666',
            },
            axisLabel: {
                rotate: 45,
                fontSize: 9,
                interval: 'auto',
            },
        },
        yAxis: yAxisConfigs,
        series: seriesData,
    };
};

// Конфигурация для area графика
export const getAreaChartOption = (
    series: any[],
    colors: string[] = CHART_COLORS,
    useMultipleAxes = true,
    showLegend = true,
    xAxisName = '', // Название оси X (например "Дата регистрации")
    showAxes = false, // Показывать ли оси Y (по умолчанию скрыты)
    showTrend: boolean | MetricDisplaySettings[] = true, // Показывать линию тренда: boolean для глобального или массив настроек для каждой метрики
    chartHeight = 450, // Высота графика для адаптации названий осей
    defaultTrendType: TrendType = 'linear', // Тип тренда по умолчанию
    alignByIndex = false // Режим сравнения: выровнять серии по номеру точки, а не по дате
) => {
    const option = getLineChartOption(series, colors, false, useMultipleAxes, showLegend, xAxisName, showAxes, showTrend, chartHeight, defaultTrendType, alignByIndex);
    if (!option) return null;

    option.series = option.series.map((s: any) => ({
        ...s,
        // Добавляем areaStyle только для основных серий, не для тренда
        areaStyle: s._isTrend ? undefined : {
            opacity: 0.3,
        },
    }));

    return option;
};

// Конфигурация для pie графика
export const getPieChartOption = (
    data: any[],
    colors: string[] = CHART_COLORS,
    showLegend = false,
    chartHeight = 450
) => {
    if (!data.length) {
        return null;
    }

    const total = data.reduce((sum, item) => sum + (item.value || 0), 0);

    // Адаптация под размер: маленький (<250), средний (250-400), большой (>400)
    const isSmall = chartHeight < 250;
    const isMedium = chartHeight >= 250 && chartHeight <= 400;

    // Для маленьких блоков уменьшаем pie и упрощаем labels
    const outerRadius = isSmall ? '65%' : isMedium ? '70%' : '75%';
    const innerRadius = isSmall ? '30%' : '35%';
    const labelFontSize = isSmall ? 10 : isMedium ? 11 : 12;
    // Порог для скрытия label мелких сегментов (в процентах)
    const minLabelPercent = isSmall ? 5 : 3;

    const pieData = data.map((item: any, index: number) => ({
        name: item.group || item.name,
        value: item.value,
        itemStyle: {
            color: item.group === 'Остальные' ? '#6E6E6E' : colors[index % colors.length],
        },
    }));

    return {
        ...getBaseChartOption(),
        tooltip: {
            trigger: 'item',
            confine: true,
            // enterable: даёт навести курсор на tooltip и прокрутить скролл при множестве меток
            enterable: true,
            extraCssText: BASE_TOOLTIP_STYLE,
            formatter: (params: any) => {
                const percent = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0;
                return `${params.name}: ${formatCellData(params.value)} (${percent}%)`;
            },
        },
        legend: {
            show: showLegend,
            orient: 'horizontal',
            bottom: 10,
        },
        series: [
            {
                type: 'pie',
                radius: [innerRadius, outerRadius],
                center: ['50%', '50%'],
                avoidLabelOverlap: true,
                itemStyle: {
                    borderRadius: 4,
                    borderColor: '#fff',
                    borderWidth: 2,
                },
                label: {
                    show: true,
                    position: 'outside',
                    formatter: (params: any) => {
                        const percent = total > 0 ? ((params.value / total) * 100) : 0;
                        // Скрываем label для мелких сегментов
                        if (percent < minLabelPercent) return '';
                        // Для маленьких блоков — только процент
                        if (isSmall) return `${percent.toFixed(0)}%`;
                        // Обрезаем длинные названия
                        const name = params.name.length > 20
                            ? params.name.slice(0, 18) + '...'
                            : params.name;
                        return `${name}\n${percent.toFixed(1)}%`;
                    },
                    fontSize: labelFontSize,
                    color: '#333',
                    lineHeight: labelFontSize + 4,
                    overflow: 'truncate',
                    ellipsis: '...',
                },
                labelLine: {
                    show: true,
                    length: isSmall ? 8 : 15,
                    length2: isSmall ? 5 : 10,
                    smooth: true,
                },
                emphasis: {
                    scale: true,
                    scaleSize: 4,
                    label: {
                        show: true,
                        fontSize: labelFontSize + 2,
                        fontWeight: 'bold',
                    },
                },
                selectedMode: false,
                data: pieData,
            },
        ],
    };
};

// Конфигурация для bar графика
export const getBarChartOption = (
    data: any[],
    colors: string[] = CHART_COLORS
) => {
    if (!data.length) {
        return null;
    }

    const groups = [...new Set(data.map((item: any) => item.group))];
    const keys = [...new Set(data.map((item: any) => item.key))];

    const seriesData = keys.map((key, index) => ({
        name: key,
        type: 'bar',
        barGap: 0,
        itemStyle: {
            color: colors[index % colors.length],
        },
        label: {
            show: true,
            position: 'inside',
            formatter: (params: any) => formatCellData(params.value),
            color: '#fff',
            fontSize: 11,
        },
        data: groups.map((group) => {
            const item = data.find((s: any) => s.group === group && s.key === key);
            return item?.value || 0;
        }),
    }));

    return {
        ...getBaseChartOption(),
        tooltip: {
            trigger: 'axis',
            confine: true,
            // enterable: даёт навести курсор на tooltip и прокрутить скролл при множестве меток
            enterable: true,
            extraCssText: BASE_TOOLTIP_STYLE,
            axisPointer: {
                type: 'shadow',
            },
            formatter: (params: any) => {
                if (!params || !params.length) return '';
                let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`;
                params.forEach((param: any) => {
                    result += `<div style="display: flex; align-items: center; margin: 4px 0;">
                        <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: ${param.color}; margin-right: 8px;"></span>
                        <span>${param.seriesName}: ${formatCellData(param.value)}</span>
                    </div>`;
                });
                return result;
            },
        },
        legend: {
            show: false,
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '20%',
            top: 10,
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            data: groups,
            axisLabel: {
                rotate: 45,
                fontSize: 11,
                interval: 0,
            },
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                formatter: (value: number) => formatCellData(value),
            },
        },
        series: seriesData,
    };
};

// Конфигурация для funnel графика
export const getFunnelChartOption = (
    data: any[],
    colors: string[] = CHART_COLORS
) => {
    if (!data.length) {
        return null;
    }

    const sortedData = [...data].sort((a: any, b: any) => b.value - a.value);
    const maxValue = Math.max(...sortedData.map((d: any) => d.value || 0));

    const funnelData = sortedData.map((item: any, index: number) => ({
        name: item.metric || item.key || item.group,
        value: item.value,
        itemStyle: {
            color: colors[index % colors.length],
        },
    }));

    return {
        ...getBaseChartOption(),
        tooltip: {
            trigger: 'item',
            confine: true,
            // enterable: даёт навести курсор на tooltip и прокрутить скролл при множестве меток
            enterable: true,
            extraCssText: BASE_TOOLTIP_STYLE,
            formatter: (params: any) => `${params.name}: ${formatCellData(params.value)}`,
        },
        legend: {
            show: false,
        },
        series: [
            {
                type: 'funnel',
                left: '10%',
                top: 60,
                bottom: 60,
                width: '80%',
                min: 0,
                max: maxValue,
                minSize: '0%',
                maxSize: '100%',
                sort: 'descending',
                gap: 2,
                label: {
                    show: true,
                    position: 'inside',
                    formatter: (params: any) => `${params.name}\n${formatCellData(params.value)}`,
                },
                itemStyle: {
                    borderColor: '#fff',
                    borderWidth: 1,
                },
                data: funnelData,
            },
        ],
    };
};

// Конфигурация для gauge графика (спидометр)
export const getGaugeChartOption = (
    value: number,
    maxValue: number,
    colors: string[] = CHART_COLORS
) => {
    const percentCompleted = Math.min(Math.round((value / maxValue) * 100), 100);

    if (isNaN(percentCompleted) || !isFinite(percentCompleted)) {
        return null;
    }

    return {
        ...getBaseChartOption(),
        animation: true,
        series: [
            {
                type: 'gauge',
                startAngle: 180,
                endAngle: 0,
                min: 0,
                max: 100,
                splitNumber: 10,
                itemStyle: {
                    color: percentCompleted >= 100 ? '#00b379' : '#339dff',
                },
                progress: {
                    show: true,
                    roundCap: true,
                    width: 18,
                },
                pointer: {
                    show: false,
                },
                axisLine: {
                    roundCap: true,
                    lineStyle: {
                        width: 18,
                        color: [[1, '#e6e6e6']],
                    },
                },
                axisTick: {
                    show: false,
                },
                splitLine: {
                    show: false,
                },
                axisLabel: {
                    show: false,
                },
                title: {
                    show: false,
                },
                detail: {
                    valueAnimation: true,
                    fontSize: 32,
                    fontWeight: 'bold',
                    formatter: `${percentCompleted}%`,
                    color: '#333',
                    offsetCenter: [0, '20%'],
                },
                data: [{ value: percentCompleted }],
            },
        ],
    };
};

// Конфигурация для treemap графика
export const getTreemapChartOption = (
    data: any,
    colors: string[] = CHART_COLORS
) => {
    if (!data?.children?.length) {
        return null;
    }

    return {
        ...getBaseChartOption(),
        tooltip: {
            confine: true,
            // enterable: даёт навести курсор на tooltip и прокрутить скролл при множестве меток
            enterable: true,
            extraCssText: BASE_TOOLTIP_STYLE,
            formatter: (params: any) => {
                return `${params.name}: ${formatCellData(params.value)}`;
            },
        },
        series: [
            {
                type: 'treemap',
                data: data.children.map((child: any, index: number) => ({
                    ...child,
                    itemStyle: {
                        color: colors[index % colors.length],
                    },
                })),
                label: {
                    show: true,
                    formatter: '{b}',
                    fontSize: 12,
                    color: '#fff',
                },
                itemStyle: {
                    borderColor: '#fff',
                    borderWidth: 2,
                    gapWidth: 2,
                },
                breadcrumb: {
                    show: false,
                },
            },
        ],
    };
};

// Утилита для очистки HTML тегов
export const cleanHtmlTags = (str: string): string => {
    if (typeof str !== 'string') return '';
    return str
        .replace(/<\/?[^>]+(>|$)/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/<span[^>]*>.*?<\/span>/gi, '');
};
